import {useCallback, useRef, useState} from 'react';
import axios from "axios";
import {Category, PdfDocument, UploadDocumentResponse} from "../models/models";
import {parseError} from "../services/http.service";
import i18n from "../i18n";
import {getCookie} from "../utils/cookies";
import {accessTokenKey} from "../core/AuthInterceptor";
import {authorizedFetch} from "../core/authorizedFetch";

const BASE_URL = process.env.REACT_APP_AI_ASSISTANT_BACKEND_URL;

export const useApi = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);

    const sendMessage = async (
        question: string,
        onUpdate: (partialResponse: string) => void,
        files: File[] | null,
        conversationId: string,
        onFinish: () => void,
        searchInternet?: boolean,
        retry = false
    ): Promise<(() => void) | undefined> => {
        setError(null);
        setLoading(true);

        const token = getCookie(accessTokenKey);
        const currentLanguage = i18n.language || 'pl';
        let url = `${BASE_URL}/chat?question=${encodeURIComponent(question)}&conversationId=${conversationId}&lang=${currentLanguage}`;
        if (searchInternet) url += `&searchInternet=${searchInternet}`;
        if (retry) url += `&retry=${retry}`;

        const formData = new FormData();
        if (files && files.length > 0) {
            files.forEach((file) => formData.append("files", file));
        }

        const controller = new AbortController();
        let cancelled = false;
        let finished = false;
        let readerReleased = false;

        const safeFinish = () => {
            if (finished) return;
            finished = true;
            setLoading(false);
            onFinish();
        };

        const releaseReader = () => {
            if (readerReleased) return;
            readerReleased = true;
            try {
                readerRef.current?.releaseLock();
            } catch { /* ignore */
            }
            readerRef.current = null;
        };

        try {
            const response = await authorizedFetch(url, {
                method: "POST",
                headers: {
                    Accept: "text/event-stream",
                },
                body: formData,
                signal: controller.signal,
            });

            if (!response.ok || !response.body) {
                throw new Error("Błąd połączenia ze strumieniem");
            }

            const reader = response.body.getReader();
            readerRef.current = reader;
            const decoder = new TextDecoder();

            await (async () => {
                let partialResponse = "";
                let buffer = "";

                try {

                    // eslint-disable-next-line no-constant-condition
                    while (true) {
                        const {done, value} = await reader.read();
                        if (done) break;

                        buffer += decoder.decode(value, {stream: true});

                        let idx: number;
                        while ((idx = buffer.indexOf("\n\n")) !== -1) {
                            const rawEvent = buffer.slice(0, idx);
                            buffer = buffer.slice(idx + 2);

                            const lines = rawEvent.split(/\r?\n/);
                            let dataPayload = "";
                            for (const line of lines) {
                                const m = line.match(/^data:(.*)$/);
                                if (m) dataPayload += m[1];
                            }

                            if (cancelled) continue;

                            if (dataPayload === "") {
                                partialResponse += "  \n";
                            } else {
                                partialResponse += dataPayload;
                            }

                            if (partialResponse.endsWith("_END_")) {
                                partialResponse = partialResponse.slice(0, -5);
                                onUpdate(partialResponse);
                                try {
                                    if (!readerReleased) {
                                        await reader.cancel();
                                        releaseReader();
                                    }
                                } catch { /* ignore */
                                }
                                controller.abort();
                                safeFinish();
                                return;
                            }

                            onUpdate(partialResponse);
                        }
                    }
                } catch {
                    if (!cancelled) onUpdate("Błąd połączenia. Spróbuj odświeżyć stronę.");
                } finally {
                    if (!readerReleased) {
                        releaseReader();
                    }
                    safeFinish();
                }
            })();

            return () => {
                cancelled = true;
                try {
                    if (!readerReleased) {
                        readerRef.current?.cancel();
                        releaseReader();
                    }
                } catch { /* ignore */
                }
                controller.abort();
            };
        } catch {
            onUpdate("Błąd połączenia. Spróbuj odświeżyć stronę.");
            safeFinish();
            return undefined;
        }
    };

    const activateAssistantVariant = async (assistantMessageId: number): Promise<void> => {
        setError(null);
        await axios.patch(`/chat/answer/${assistantMessageId}/activate`);
    };

    const fetchCategories = useCallback(async (): Promise<Category[]> => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get<Category[]>(`/categories`);
            return response.data;
        } catch (err: any) {
            setError(parseError(err));
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDocumentsByCategoryId = async (id: string): Promise<PdfDocument[]> => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get<PdfDocument[]>(`/categories/${id}/documents`);
            return response.data;
        } catch (err: any) {
            setError(parseError(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const downloadDocumentForId = async (id: number, fileName: string): Promise<void> => {
        setLoading(true);
        setError(null);

        try {
            const res = await axios.get(`${BASE_URL}/documents/${id}/download`, {responseType: 'blob'});
            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(parseError(err));
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const uploadDocument = async (files: File[], dataSourceId?: string): Promise<UploadDocumentResponse[]> => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        if (dataSourceId) formData.append('dataSourceId', dataSourceId);

        const response = await axios.post<UploadDocumentResponse[]>('/documents', formData, {
            headers: {'Content-Type': 'multipart/form-data'},
        });

        return response.data;
    };

    const attachDocumentToConversation = async (files: File[], conversationId?: string): Promise<UploadDocumentResponse[]> => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        if (conversationId) formData.append('conversationId', conversationId);

        const response = await axios.post<UploadDocumentResponse[]>('/documents/conversation', formData, {
            headers: {'Content-Type': 'multipart/form-data'},
        });

        return response.data;
    };

    const makePayment = async (amount: number): Promise<{ paymentUrl: string, transactionId: string }> => {
        const response = await axios.post('/payments', {amount});
        return response.data;
    };

    return {
        loading,
        error,
        sendMessage,
        activateAssistantVariant,
        fetchCategories,
        fetchDocumentsByCategoryId,
        downloadDocumentForId,
        uploadDocument,
        attachDocumentToConversation,
        makePayment
    };
};
